// Portal navigation moves the URL first and settles the server step behind it. Step changes
// run one at a time so two never race on the server, and a click made while one is in flight
// replaces any queued target rather than walking through it. A task that outlives the timeout
// rejects, and the queue moves on.

type Listener = () => void

const TASK_TIMEOUT_MS = 75_000
const NAV_TIMED_OUT = 'That page is taking too long to open. Please try again.'

let chain: Promise<void> = Promise.resolve()
let latestTicket = 0
let inFlight = 0
const listeners = new Set<Listener>()

function emit() {
  listeners.forEach(listener => listener())
}

async function withTimeout(task: Promise<void>): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      task,
      new Promise<void>((_, reject) => {
        timer = setTimeout(() => reject(new Error(NAV_TIMED_OUT)), TASK_TIMEOUT_MS)
      }),
    ])
  } finally {
    clearTimeout(timer)
  }
}

export function queuePortalNav(task: (isLatest: () => boolean) => Promise<void>): Promise<void> {
  const ticket = ++latestTicket
  const isLatest = () => ticket === latestTicket
  inFlight += 1
  emit()
  const previous = chain
  const run = (async () => {
    await previous
    if (isLatest()) {
      await withTimeout(task(isLatest))
    }
  })()
  const settle = () => {
    inFlight -= 1
    emit()
  }
  chain = run.then(settle, settle)
  return run
}

export function isPortalNavPending(): boolean {
  return inFlight > 0
}

export function whenPortalNavSettled(): Promise<void> {
  return chain
}

export function subscribePortalNav(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
